import { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { useTableSort, SortableHeader } from "@/lib/tableSort";
import { calculateBrandStats, CANONICAL_BRANDS } from "@/lib/brandStats";
import { isFreebieSkuId } from "@/lib/freebieCalc";
import { computeSellerStats } from "@/lib/sellerRanking";
import { ArrowLeft, Search, Download, IndianRupee, Percent, Package, ShoppingCart, TrendingUp, MapPin, Boxes, Calendar } from "lucide-react";

const fmtMoney = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtNum = (v) => Number(v || 0).toLocaleString("en-IN");
const fmtPct = (v) => `${Number(v || 0).toFixed(2)}%`;
const dOnly = (iso) => (iso ? String(iso).substring(0, 10) : "");

export default function SellerDrillDown() {
  const { sellerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [quotations, setQuotations] = useState([]);
  const [seller, setSeller] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [invSearch, setInvSearch] = useState("");

  const decodedId = decodeURIComponent(sellerId);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [q, s, p] = await Promise.all([
          axios.get(`${API}/quotations`, { withCredentials: true }),
          axios.get(`${API}/sellers`, { withCredentials: true }),
          axios.get(`${API}/products`, { withCredentials: true }),
        ]);
        setQuotations(q.data || []);
        setProducts(p.data || []);
        setSellers(s.data || []);
        const found = (s.data || []).find((x) => x.id === decodedId);
        setSeller(found || null);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedId]);

  // seller_id -> seller record, used to resolve each quotation's canonical seller name.
  const sellerMap = useMemo(() => {
    const m = {};
    (sellers || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [sellers]);

  // The Seller Revenue Ranking groups by seller NAME (see lib/sellerRanking.computeSellerStats),
  // because the same seller can exist under multiple seller_ids (seller_code is not globally
  // unique). The drill-down MUST aggregate by the same name so totals reconcile with the ranking.
  const targetName = useMemo(() => {
    const byId = sellerMap[decodedId]?.seller_name;
    return String(byId || location.state?.sellerName || decodedId || "").trim();
  }, [sellerMap, decodedId, location.state]);

  // Invoiced quotations for THIS seller (matched by NAME) within the selected date range (invoice_date).
  const sellerQuotations = useMemo(() => {
    return quotations.filter((q) => {
      if (!q.invoice_date) return false;
      const name = String(sellerMap[q.seller_id]?.seller_name || q.seller_name || "Unknown").trim();
      if (name !== targetName) return false;
      const d = dOnly(q.invoice_date);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });
  }, [quotations, sellerMap, targetName, dateFrom, dateTo]);

  // ---- SINGLE SOURCE OF TRUTH ----
  // Every KPI, the Brand-wise table, and the Invoice-History brand chips are derived from the
  // SAME per-quotation brand breakdown (lib/brandStats.calculateBrandStats) that the Quotation
  // Detail page uses. This guarantees values reconcile across sections and match the quotation.
  const perQuotation = useMemo(() => sellerQuotations.map((q) => ({
    q,
    stats: calculateBrandStats(q.items, q.freebies), // { brand: {mrp, invoice, discount} } eligible-only
  })), [sellerQuotations]);

  // Brand-wise aggregation across the seller's filtered quotations (discount-eligible basis).
  const brandRows = useMemo(() => {
    const agg = {};
    perQuotation.forEach(({ q, stats }) => {
      const unitsByBrand = {};
      (q.items || []).forEach((it) => {
        if (it.exclude_from_discount) return;
        if (isFreebieSkuId(it.sku_id)) return;
        if (!CANONICAL_BRANDS.includes(it.brand)) return;
        unitsByBrand[it.brand] = (unitsByBrand[it.brand] || 0) + (Number(it.quantity) || 0);
      });
      Object.entries(stats).forEach(([b, s]) => {
        if (!agg[b]) agg[b] = { brand: b, revenue: 0, mrp: 0, units: 0, quotations: 0 };
        agg[b].revenue += s.invoice;
        agg[b].mrp += s.mrp;
        agg[b].units += unitsByBrand[b] || 0;
        agg[b].quotations += 1;
      });
    });
    const totalBrandRevenue = Object.values(agg).reduce((s, r) => s + r.revenue, 0);
    return Object.values(agg).map((r) => ({
      ...r,
      discount: r.mrp > 0 ? ((r.mrp - r.revenue) / r.mrp) * 100 : 0,
      contribution: totalBrandRevenue > 0 ? (r.revenue / totalBrandRevenue) * 100 : 0,
    }));
  }, [perQuotation]);

  // Top-line KPIs reuse the SAME aggregation as Seller Revenue Ranking (computeSellerStats),
  // guaranteeing Total Revenue / MRP / Units / Quotations / Discount% reconcile 100% with it.
  const kpis = useMemo(() => {
    const stat = computeSellerStats(sellerQuotations, sellerMap, products, {})[0] || null;
    const count = stat?.quotationCount || 0;
    const lastOrder = sellerQuotations.reduce((mx, q) => {
      const d = dOnly(q.invoice_date);
      return d > mx ? d : mx;
    }, "");
    return {
      totalRevenue: stat?.totalRevenue || 0,
      totalMrp: stat?.totalMrp || 0,
      overallDiscount: stat?.overallDiscount || 0,
      totalUnits: stat?.totalUnits || 0,
      avgOrderValue: count > 0 ? (stat?.totalRevenue || 0) / count : 0,
      avgDiscount: stat?.avgEffectiveDiscount || 0,
      count,
      brandsPurchased: brandRows.length,
      lastOrderDate: lastOrder,
    };
  }, [sellerQuotations, sellerMap, products, brandRows]);

  const { sortedRows: brandSorted, sort: brandSort, toggleSort: toggleBrandSort } = useTableSort(brandRows, {
    defaultKey: "revenue", defaultOrder: "desc",
    accessors: {
      brand: (r) => r.brand, revenue: (r) => r.revenue, mrp: (r) => r.mrp,
      discount: (r) => r.discount, units: (r) => r.units, quotations: (r) => r.quotations,
      contribution: (r) => r.contribution,
    },
  });

  // Invoice history — brand chips + overall discount come from the SAME saved quotation values.
  const invoiceRows = useMemo(() => {
    return perQuotation.map(({ q, stats }) => ({
      id: q.id,
      invoice_date: q.invoice_date,
      invoice_number: q.invoice_number || "—",
      quotation_id: q.quotation_id || "—",
      // Overall discount = the value saved on the quotation (effective_discount), not recomputed.
      discount: q.effective_discount != null
        ? q.effective_discount
        : ((q.total_mrp_value || 0) > 0 ? (((q.total_mrp_value || 0) - (q.total_invoice_value || 0)) / (q.total_mrp_value || 0)) * 100 : 0),
      brandDiscounts: Object.entries(stats)
        .map(([brand, s]) => ({ brand, discount: s.discount }))
        .sort((a, b) => b.discount - a.discount),
      remarks: (q.remarks || q.approval_remarks || "").trim(),
      remark_categories: q.remark_categories || [],
    }));
  }, [perQuotation]);

  const searchedInvoices = useMemo(() => {
    const s = invSearch.trim().toLowerCase();
    if (!s) return invoiceRows;
    return invoiceRows.filter((r) =>
      `${r.invoice_number} ${r.quotation_id} ${r.remarks} ${r.remark_categories.join(" ")} ${r.brandDiscounts.map((b) => b.brand).join(" ")}`.toLowerCase().includes(s)
    );
  }, [invoiceRows, invSearch]);

  const { sortedRows: invSorted, sort: invSort, toggleSort: toggleInvSort } = useTableSort(searchedInvoices, {
    defaultKey: "invoice_date", defaultOrder: "desc",
    accessors: {
      invoice_date: (r) => r.invoice_date || "", invoice_number: (r) => r.invoice_number || "",
      quotation_id: (r) => r.quotation_id || "", discount: (r) => r.discount,
      brandDiscounts: (r) => (r.brandDiscounts[0]?.discount ?? -1),
      remarks: (r) => r.remarks || "", remark_categories: (r) => (r.remark_categories[0] || ""),
    },
  });

  const exportInvoices = () => {
    const data = invSorted.map((r) => ({
      "Invoice Date": dOnly(r.invoice_date),
      "Invoice Number": r.invoice_number,
      "Quotation ID": r.quotation_id,
      "Overall Discount %": Number(r.discount.toFixed(2)),
      "Brand-wise Discount %": r.brandDiscounts.map((b) => `${b.brand}: ${b.discount.toFixed(2)}%`).join(", "),
      "Remarks": r.remarks || "",
      "Remark Category": r.remark_categories.join(", "),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoice History");
    const name = String(targetName || seller?.seller_name || "Seller").replace(/[^a-z0-9]+/gi, "_");
    XLSX.writeFile(wb, `${name}_Invoices${dateFrom || dateTo ? `_${dateFrom || "start"}_${dateTo || "end"}` : ""}.xlsx`);
  };

  const KPI = ({ icon: Icon, label, value, sub, testId }) => (
    <Card className="border-[#E8E8E8]">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          <Icon className="h-4 w-4" />
          <span className="text-xs">{label}</span>
        </div>
        <p className="text-xl font-bold font-mono" data-testid={testId}>{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="seller-drill-down-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" className="h-7 px-2 -ml-2 mb-1 text-muted-foreground" onClick={() => navigate(-1)} data-testid="sdd-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Seller Revenue Ranking
          </Button>
          <h1 className="text-2xl font-bold tracking-tight font-heading" data-testid="sdd-seller-name">
            {seller?.seller_name || location.state?.sellerName || "Seller"}
          </h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
            <span data-testid="sdd-seller-code">Code: <span className="font-mono text-slate-700">{seller?.seller_code || "—"}</span></span>
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{seller?.city || "—"}, {seller?.state || "—"}</span>
          </div>
        </div>
        <DateRangeFilter from={dateFrom} to={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} testId="sdd-date-range" />
      </div>

      {/* 1. Business Summary */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-4">
        <KPI icon={IndianRupee} label="Total Revenue" value={fmtMoney(kpis.totalRevenue)} testId="sdd-kpi-revenue" />
        <KPI icon={IndianRupee} label="Total MRP" value={fmtMoney(kpis.totalMrp)} testId="sdd-kpi-mrp" />
        <KPI icon={Percent} label="Overall Discount %" value={fmtPct(kpis.overallDiscount)} testId="sdd-kpi-discount" />
        <KPI icon={Package} label="Total Units Sold" value={fmtNum(kpis.totalUnits)} testId="sdd-kpi-units" />
        <KPI icon={ShoppingCart} label="Total Quotations" value={fmtNum(kpis.count)} testId="sdd-kpi-quotations" />
        <KPI icon={ShoppingCart} label="Avg Order Value" value={fmtMoney(kpis.avgOrderValue)} testId="sdd-kpi-aov" />
        <KPI icon={TrendingUp} label="Avg Discount %" value={fmtPct(kpis.avgDiscount)} testId="sdd-kpi-avgdisc" />
        <KPI icon={Boxes} label="Brands Purchased" value={fmtNum(kpis.brandsPurchased)} testId="sdd-kpi-brands" />
        <KPI icon={Calendar} label="Last Order Date" value={kpis.lastOrderDate || "—"} testId="sdd-kpi-lastorder" />
      </div>

      {/* 2. Brand-wise Discount Analysis */}
      <Card className="border-[#E8E8E8]">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Brand-wise Analysis</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10">
                <TableRow className="bg-slate-100 hover:bg-slate-100">
                  <SortableHeader sortKey="brand" sort={brandSort} onToggle={toggleBrandSort} className="bg-slate-100 border-b-2 border-slate-200">Brand</SortableHeader>
                  <SortableHeader sortKey="revenue" sort={brandSort} onToggle={toggleBrandSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Revenue</SortableHeader>
                  <SortableHeader sortKey="mrp" sort={brandSort} onToggle={toggleBrandSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">MRP</SortableHeader>
                  <SortableHeader sortKey="discount" sort={brandSort} onToggle={toggleBrandSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Discount %</SortableHeader>
                  <SortableHeader sortKey="units" sort={brandSort} onToggle={toggleBrandSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Units Sold</SortableHeader>
                  <SortableHeader sortKey="quotations" sort={brandSort} onToggle={toggleBrandSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Quotations</SortableHeader>
                  <SortableHeader sortKey="contribution" sort={brandSort} onToggle={toggleBrandSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Revenue Contrib %</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brandSorted.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm" data-testid="sdd-brand-empty">No brand data for the selected range.</TableCell></TableRow>
                ) : (
                  brandSorted.map((r) => (
                    <TableRow key={r.brand} data-testid={`sdd-brand-row-${r.brand}`}>
                      <TableCell className="text-xs font-medium">{r.brand}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtMoney(r.revenue)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtMoney(r.mrp)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">
                        {r.discount > 62 ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">{fmtPct(r.discount)}</span>
                        ) : fmtPct(r.discount)}
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtNum(r.units)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtNum(r.quotations)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{fmtPct(r.contribution)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* 3. Invoice History */}
      <Card className="border-[#E8E8E8]">
        <CardHeader className="pb-2 flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">Invoice History <span className="text-xs font-normal text-muted-foreground">({invSorted.length})</span></CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={invSearch} onChange={(e) => setInvSearch(e.target.value)} placeholder="Search invoice, quotation, status..." className="h-8 pl-8 text-xs w-64" data-testid="sdd-invoice-search" />
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={exportInvoices} disabled={invSorted.length === 0} data-testid="sdd-invoice-export">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export to Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[55vh]">
            <Table>
              <TableHeader className="sticky top-0 z-10">
                <TableRow className="bg-slate-100 hover:bg-slate-100">
                  <SortableHeader sortKey="invoice_date" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Invoice Date</SortableHeader>
                  <SortableHeader sortKey="invoice_number" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Invoice #</SortableHeader>
                  <SortableHeader sortKey="quotation_id" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Quotation ID</SortableHeader>
                  <SortableHeader sortKey="discount" sort={invSort} onToggle={toggleInvSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Overall Disc %</SortableHeader>
                  <SortableHeader sortKey="brandDiscounts" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Brand-wise Discount %</SortableHeader>
                  <SortableHeader sortKey="remarks" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Remarks</SortableHeader>
                  <SortableHeader sortKey="remark_categories" sort={invSort} onToggle={toggleInvSort} className="bg-slate-100 border-b-2 border-slate-200">Remark Category</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm">Loading…</TableCell></TableRow>
                ) : invSorted.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm" data-testid="sdd-invoice-empty">No invoices for the selected range.</TableCell></TableRow>
                ) : (
                  invSorted.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer hover:bg-[#14b8a6]/5 align-top" onClick={() => navigate(`/quotations/${r.id}`)} data-testid={`sdd-invoice-row-${r.quotation_id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{dOnly(r.invoice_date)}</TableCell>
                      <TableCell className="text-xs font-medium whitespace-nowrap">{r.invoice_number}</TableCell>
                      <TableCell className="text-xs text-[#14b8a6] whitespace-nowrap">{r.quotation_id}</TableCell>
                      <TableCell className={`text-right text-xs font-mono ${r.discount > 62 ? "text-red-700 font-semibold" : ""}`}>{fmtPct(r.discount)}</TableCell>
                      <TableCell className="text-xs">
                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                          {r.brandDiscounts.length === 0 ? <span className="text-muted-foreground">—</span> : r.brandDiscounts.map((b) => (
                            <span key={b.brand} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${b.discount > 62 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>
                              {b.brand} <span className="font-mono">{b.discount.toFixed(1)}%</span>
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs max-w-[240px]">
                        {r.remarks ? <span className="text-slate-700 whitespace-pre-wrap break-words">{r.remarks}</span> : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {r.remark_categories.length === 0 ? <span className="text-muted-foreground">—</span> : r.remark_categories.map((c) => (
                            <span key={c} className="inline-flex px-1.5 py-0.5 rounded-full text-[11px] bg-[#14b8a6]/10 text-[#0e7c74]">{c}</span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
